import React, { useState, useRef, useEffect, useContext } from 'react';
import { Camera, X, RotateCw, Zap, ZapOff, CheckCircle } from 'lucide-react';
import { AuthContext } from '../context/AuthContext';
import { API_URL } from '../config/api';

export default function CardScanner({ onClose, onScanComplete, publicToken = null }) {
  // Use context directly - works even without AuthProvider (returns undefined)
  const auth = useContext(AuthContext);
  const csrfToken = auth?.csrfToken;
  const videoRef = useRef(null);
  const canvasRef = useRef(null);
  const [stream, setStream] = useState(null);
  const [facingMode, setFacingMode] = useState('environment'); // 'user' or 'environment'
  const [flashEnabled, setFlashEnabled] = useState(false);
  const [capturing, setCapturing] = useState(false);
  const [processing, setProcessing] = useState(false);
  const [error, setError] = useState(null);
  const [instruction, setInstruction] = useState('Aseta kortti kehyksen sisään');
  const [imageCaptured, setImageCaptured] = useState(false);
  const [croppedImageUrl, setCroppedImageUrl] = useState(null);

  useEffect(() => {
    startCamera();
    return () => {
      stopCamera();
    };
  }, [facingMode]);

  const startCamera = async () => {
    try {
      setError(null);

      // Stop existing stream
      if (stream) {
        stream.getTracks().forEach(track => track.stop());
      }

      const constraints = {
        video: {
          facingMode: facingMode,
          width: { ideal: 1920 },
          height: { ideal: 1080 }
        }
      };

      const mediaStream = await navigator.mediaDevices.getUserMedia(constraints);
      setStream(mediaStream);

      if (videoRef.current) {
        videoRef.current.srcObject = mediaStream;
      }
    } catch (err) {
      console.error('Camera error:', err);
      setError('Kameran käynnistys epäonnistui. Tarkista käyttöoikeudet.');
    }
  };

  const stopCamera = () => {
    if (stream) {
      stream.getTracks().forEach(track => track.stop());
      setStream(null);
    }
  };

  const toggleFlash = async () => {
    if (!stream) return;

    const track = stream.getVideoTracks()[0];
    const capabilities = track.getCapabilities();

    if (capabilities.torch) {
      try {
        await track.applyConstraints({
          advanced: [{ torch: !flashEnabled }]
        });
        setFlashEnabled(!flashEnabled);
      } catch (err) {
        console.error('Flash toggle error:', err);
      }
    }
  };

  const flipCamera = () => {
    setFacingMode(facingMode === 'environment' ? 'user' : 'environment');
  };

  const captureImage = async () => {
    if (!videoRef.current || capturing || processing) return;

    setCapturing(true);
    setInstruction('📸 Kuva otettu - Analysoidaan...');

    // Wait a moment for user to stabilize
    await new Promise(resolve => setTimeout(resolve, 300));

    const video = videoRef.current;
    const canvas = canvasRef.current;
    const context = canvas.getContext('2d');

    // Set canvas size to video size (full resolution)
    canvas.width = video.videoWidth;
    canvas.height = video.videoHeight;

    // Draw video frame to canvas
    context.drawImage(video, 0, 0, canvas.width, canvas.height);

    // Freeze the camera view - show canvas instead of video
    setImageCaptured(true);

    // Convert full image to blob for AI scanning
    canvas.toBlob(async (fullImageBlob) => {
      if (!fullImageBlob) {
        setError('Kuvan ottaminen epäonnistui');
        setCapturing(false);
        setImageCaptured(false);
        return;
      }

      // Process with AI and crop
      await processImageAndCrop(fullImageBlob, canvas);
    }, 'image/jpeg', 0.95);
  };

  const processImageAndCrop = async (fullImageBlob, fullCanvas) => {
    setProcessing(true);
    setInstruction('🔍 Analysoidaan korttia...');

    try {
      // Step 1: Send full image to AI for scanning
      const formData = new FormData();
      formData.append('image', fullImageBlob, 'card.jpg');

      // Use public or authenticated endpoint based on mode
      let response;
      if (publicToken) {
        // Public mode - use token-based API
        formData.append('token', publicToken);
        response = await fetch(`${API_URL}/public/scan-card.php`, {
          method: 'POST',
          body: formData
        });
      } else {
        // Authenticated mode - use CSRF protected API
        formData.append('csrf_token', csrfToken);
        response = await fetch(`${API_URL}/certifications/scan-card.php`, {
          method: 'POST',
          credentials: 'include',
          headers: {
            'X-CSRF-Token': csrfToken || ''
          },
          body: formData
        });
      }

      if (!response.ok) {
        throw new Error(`Server error: ${response.status}`);
      }

      const data = await response.json();

      if (!data.success) {
        throw new Error(data.message || 'Skannaus epäonnistui');
      }

      setInstruction('✂️ Rajataan kuvaa...');

      // Step 2: Crop image to card overlay area
      const croppedBlob = await cropToCardArea(fullCanvas);

      setInstruction('✅ Kortti skannattu!');

      // Use cropped image if available, otherwise use full image
      const finalBlob = croppedBlob || fullImageBlob;
      const previewUrl = croppedBlob ? URL.createObjectURL(croppedBlob) : URL.createObjectURL(fullImageBlob);

      // Call parent callback with extracted data and image
      if (onScanComplete) {
        onScanComplete({
          extractedData: data.data,
          imageBlob: finalBlob,
          croppedImageUrl: previewUrl
        });
      }

      // Close after short delay
      setTimeout(() => {
        stopCamera();
        onClose();
      }, 1000);

    } catch (err) {
      console.error('Scan error:', err);
      setError(err.message || 'Verkkovirhe. Yritä uudelleen.');
      setInstruction('Virhe - Yritä uudelleen');
      setImageCaptured(false); // Show live camera again
    } finally {
      setCapturing(false);
      setProcessing(false);
    }
  };

  const cropToCardArea = async (fullCanvas) => {
    try {
      const video = videoRef.current;
      if (!video) return null;

      // Calculate card overlay dimensions
      // The overlay is: width: 85.6mm, maxWidth: 90%, aspectRatio: 85.6/53.98
      const containerWidth = window.innerWidth;
      const containerHeight = window.innerHeight;

      // Card dimensions (matching the overlay)
      const cardAspectRatio = 85.6 / 53.98; // ~1.586
      let cardOverlayWidth = Math.min(containerWidth * 0.9, 85.6 * 3.7795275591); // mm to px approximation
      let cardOverlayHeight = cardOverlayWidth / cardAspectRatio;

      // Ensure it fits in viewport
      if (cardOverlayHeight > containerHeight * 0.7) {
        cardOverlayHeight = containerHeight * 0.7;
        cardOverlayWidth = cardOverlayHeight * cardAspectRatio;
      }

      // Calculate center position (overlay is centered)
      const centerX = containerWidth / 2;
      const centerY = containerHeight / 2;

      // Overlay position
      const overlayLeft = centerX - (cardOverlayWidth / 2);
      const overlayTop = centerY - (cardOverlayHeight / 2);

      // Map overlay coordinates to video/canvas coordinates
      const scaleX = fullCanvas.width / containerWidth;
      const scaleY = fullCanvas.height / containerHeight;

      const cropX = overlayLeft * scaleX;
      const cropY = overlayTop * scaleY;
      const cropWidth = cardOverlayWidth * scaleX;
      const cropHeight = cardOverlayHeight * scaleY;

      // Create a new canvas for the cropped image
      const croppedCanvas = document.createElement('canvas');
      croppedCanvas.width = cropWidth;
      croppedCanvas.height = cropHeight;
      const croppedContext = croppedCanvas.getContext('2d');

      // Draw the cropped portion
      croppedContext.drawImage(
        fullCanvas,
        cropX, cropY, cropWidth, cropHeight,  // Source
        0, 0, cropWidth, cropHeight            // Destination
      );

      // Convert to blob
      return new Promise((resolve) => {
        croppedCanvas.toBlob((blob) => {
          resolve(blob);
        }, 'image/jpeg', 0.92);
      });

    } catch (err) {
      console.error('Crop error:', err);
      console.error('Canvas dimensions:', fullCanvas?.width, 'x', fullCanvas?.height);
      console.error('Container dimensions:', window.innerWidth, 'x', window.innerHeight);
      return null; // Return null to fallback to full image
    }
  };

  return (
    <div style={{
      position: 'fixed',
      top: 0,
      left: 0,
      right: 0,
      bottom: 0,
      background: '#000',
      zIndex: 9999,
      display: 'flex',
      flexDirection: 'column'
    }}>
      {/* Header */}
      <div style={{
        padding: '1rem',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        background: 'rgba(0,0,0,0.8)',
        color: 'white'
      }}>
        <button
          onClick={onClose}
          style={{
            background: 'transparent',
            border: 'none',
            color: 'white',
            cursor: 'pointer',
            display: 'flex',
            alignItems: 'center',
            gap: '0.5rem',
            fontSize: '16px'
          }}
        >
          <X size={24} />
          Peruuta
        </button>
        <h2 style={{ margin: 0, fontSize: '18px' }}>Skannaa kortti</h2>
        <div style={{ width: '80px' }} /> {/* Spacer */}
      </div>

      {/* Camera View */}
      <div style={{
        flex: 1,
        position: 'relative',
        overflow: 'hidden',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center'
      }}>
        <video
          ref={videoRef}
          autoPlay
          playsInline
          muted
          style={{
            width: '100%',
            height: '100%',
            objectFit: 'cover',
            display: imageCaptured ? 'none' : 'block'
          }}
        />

        {/* Canvas - shown when image is captured (frozen frame) */}
        <canvas
          ref={canvasRef}
          style={{
            width: '100%',
            height: '100%',
            objectFit: 'cover',
            display: imageCaptured ? 'block' : 'none'
          }}
        />

        {/* Card Overlay Guide */}
        <div style={{
          position: 'absolute',
          top: '50%',
          left: '50%',
          transform: 'translate(-50%, -50%)',
          width: '85.6mm', // Credit card width
          maxWidth: '90%',
          aspectRatio: '85.6 / 53.98', // Credit card dimensions
          border: '3px solid rgba(255, 255, 255, 0.8)',
          borderRadius: '12px',
          pointerEvents: 'none',
          boxShadow: '0 0 0 9999px rgba(0, 0, 0, 0.5)' // Darken outside
        }}>
          {/* Corner markers */}
          {['topLeft', 'topRight', 'bottomLeft', 'bottomRight'].map((corner) => (
            <div
              key={corner}
              style={{
                position: 'absolute',
                width: '20px',
                height: '20px',
                borderColor: '#00ff00',
                borderStyle: 'solid',
                ...(corner === 'topLeft' && {
                  top: '-3px',
                  left: '-3px',
                  borderWidth: '3px 0 0 3px',
                  borderRadius: '12px 0 0 0'
                }),
                ...(corner === 'topRight' && {
                  top: '-3px',
                  right: '-3px',
                  borderWidth: '3px 3px 0 0',
                  borderRadius: '0 12px 0 0'
                }),
                ...(corner === 'bottomLeft' && {
                  bottom: '-3px',
                  left: '-3px',
                  borderWidth: '0 0 3px 3px',
                  borderRadius: '0 0 0 12px'
                }),
                ...(corner === 'bottomRight' && {
                  bottom: '-3px',
                  right: '-3px',
                  borderWidth: '0 3px 3px 0',
                  borderRadius: '0 0 12px 0'
                })
              }}
            />
          ))}
        </div>

        {/* Error Message */}
        {error && (
          <div style={{
            position: 'absolute',
            top: '1rem',
            left: '1rem',
            right: '1rem',
            padding: '1rem',
            background: 'rgba(220, 38, 38, 0.9)',
            color: 'white',
            borderRadius: '8px',
            textAlign: 'center'
          }}>
            {error}
          </div>
        )}
      </div>

      {/* Instructions */}
      <div style={{
        padding: '1rem',
        background: 'rgba(0,0,0,0.8)',
        color: 'white',
        textAlign: 'center',
        fontSize: '16px',
        fontWeight: '500'
      }}>
        {instruction}
      </div>

      {/* Controls */}
      <div style={{
        padding: '1.5rem',
        background: 'rgba(0,0,0,0.9)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-around'
      }}>
        {/* Flash Toggle */}
        <button
          onClick={toggleFlash}
          disabled={processing}
          style={{
            background: flashEnabled ? '#fbbf24' : 'transparent',
            border: '2px solid white',
            color: 'white',
            width: '56px',
            height: '56px',
            borderRadius: '50%',
            cursor: processing ? 'not-allowed' : 'pointer',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            opacity: processing ? 0.5 : 1
          }}
        >
          {flashEnabled ? <Zap size={24} /> : <ZapOff size={24} />}
        </button>

        {/* Capture Button */}
        <button
          onClick={captureImage}
          disabled={capturing || processing}
          style={{
            background: 'white',
            border: '4px solid #6366f1',
            width: '72px',
            height: '72px',
            borderRadius: '50%',
            cursor: (capturing || processing) ? 'not-allowed' : 'pointer',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            opacity: (capturing || processing) ? 0.5 : 1,
            position: 'relative'
          }}
        >
          {processing ? (
            <div style={{
              width: '32px',
              height: '32px',
              border: '3px solid #6366f1',
              borderTopColor: 'transparent',
              borderRadius: '50%',
              animation: 'spin 1s linear infinite'
            }} />
          ) : (
            <Camera size={32} color="#6366f1" />
          )}
        </button>

        {/* Flip Camera */}
        <button
          onClick={flipCamera}
          disabled={processing}
          style={{
            background: 'transparent',
            border: '2px solid white',
            color: 'white',
            width: '56px',
            height: '56px',
            borderRadius: '50%',
            cursor: processing ? 'not-allowed' : 'pointer',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            opacity: processing ? 0.5 : 1
          }}
        >
          <RotateCw size={24} />
        </button>
      </div>

      {/* Hidden canvas for capturing */}

      {/* CSS Animation */}
      <style>{`
        @keyframes spin {
          to { transform: rotate(360deg); }
        }
      `}</style>
    </div>
  );
}
